/**
 * WhiteLabelPage (M10, RF-M10-006, RN-M10-002) — configuração white-label
 * (admin): nome do sistema, cor primária (hex) e o ARQUIVO do logo (PNG/JPEG até
 * 512 KB, enviado em base64 no PATCH /config — contrato revisado, C-M1; antes a
 * tela pedia uma "chave S3"). Preview ao vivo da cor/nome e do logo (o salvo, ou o
 * arquivo escolhido antes de salvar — pedido do usuário, 01/10/2026). Ao salvar, a config
 * reflete no cabeçalho/login sem redeploy (ADR-016). Guarda de perfil na rota.
 */

import { FormEvent, useEffect, useState } from 'react';
import { useAtualizarConfig, useConfig } from '../hooks/usePainel';
import { ApiError } from '../services/apiClient';
import { LogoArquivo } from '../services/painel-types';

const HEX = /^#[0-9a-fA-F]{6}$/;
const LOGO_MAX_BYTES = 512 * 1024;
const TIPOS_LOGO: readonly string[] = ['image/png', 'image/jpeg'];

/** Bytes → base64 em blocos (evita estourar a pilha com arquivos grandes). */
async function paraBase64(arquivo: File): Promise<string> {
  const bytes = new Uint8Array(await arquivo.arrayBuffer());
  let binario = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binario += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binario);
}

export function WhiteLabelPage(): JSX.Element {
  const { config, carregando } = useConfig();
  const { atualizar, enviando } = useAtualizarConfig();

  const [nomeSistema, setNomeSistema] = useState('');
  const [corPrimaria, setCorPrimaria] = useState('#660099');
  const [logo, setLogo] = useState<File | null>(null);
  const [previaLogo, setPreviaLogo] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  /** Arquivo escolhido: guarda para o envio e mostra a prévia (só PNG/JPEG). */
  function escolherLogo(arquivo: File | null): void {
    setLogo(arquivo);
    setPreviaLogo(null);
    if (arquivo && TIPOS_LOGO.includes(arquivo.type)) {
      const leitor = new FileReader();
      leitor.onload = () => setPreviaLogo(typeof leitor.result === 'string' ? leitor.result : null);
      leitor.readAsDataURL(arquivo);
    }
  }

  // Prévia: o arquivo novo (ainda não salvo) ou o logo salvo (URL do GET /config).
  const logoSalvo = config?.logoUrl ? config.logoUrl : null;
  const logoExibido = previaLogo ?? logoSalvo;

  // Preenche os campos quando a config carrega (uma vez por carga).
  useEffect(() => {
    if (config) {
      setNomeSistema(config.nomeSistema);
      if (HEX.test(config.corPrimaria)) {
        setCorPrimaria(config.corPrimaria);
      }
    }
  }, [config]);

  async function salvar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setMensagem(null);
    if (!HEX.test(corPrimaria)) {
      setMensagem({ tipo: 'erro', texto: 'A cor primária deve estar no formato #RRGGBB.' });
      return;
    }
    if (logo && (!TIPOS_LOGO.includes(logo.type) || logo.size === 0 || logo.size > LOGO_MAX_BYTES)) {
      setMensagem({ tipo: 'erro', texto: 'O logo deve ser um arquivo PNG ou JPEG de até 512 KB.' });
      return;
    }
    try {
      const arquivoLogo: LogoArquivo | undefined = logo
        ? { contentType: logo.type as LogoArquivo['contentType'], conteudoBase64: await paraBase64(logo) }
        : undefined;
      await atualizar({
        nomeSistema: nomeSistema.trim() || undefined,
        corPrimaria,
        logo: arquivoLogo,
      });
      setLogo(null);
      setPreviaLogo(null);
      setMensagem({ tipo: 'ok', texto: 'Configuração salva. O tema é aplicado sem redeploy.' });
    } catch (err) {
      const texto =
        err instanceof ApiError && err.status === 403
          ? 'Seu perfil não tem permissão para alterar o white-label.'
          : 'Não foi possível salvar a configuração. Tente novamente.';
      setMensagem({ tipo: 'erro', texto });
    }
  }

  return (
    <div>
      <h1 className="page-title">White-label</h1>
      <p className="page-subtitle">
        Personalize o nome, a cor primária e o logo do sistema. As alterações refletem no cabeçalho
        e no login sem novo deploy.
      </p>

      {carregando && <p className="muted">Carregando…</p>}

      <form className="card" onSubmit={(e) => void salvar(e)} aria-label="Configuração white-label">
        <div className="form-field">
          <label htmlFor="nomeSistema">Nome do sistema</label>
          <input
            id="nomeSistema"
            type="text"
            maxLength={120}
            value={nomeSistema}
            onChange={(e) => setNomeSistema(e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="corPrimaria">Cor primária (#RRGGBB)</label>
          <input
            id="corPrimaria"
            type="text"
            value={corPrimaria}
            onChange={(e) => setCorPrimaria(e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="logoArquivo">Logo (PNG ou JPEG, até 512 KB — opcional)</label>
          <input
            id="logoArquivo"
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => escolherLogo(e.target.files?.[0] ?? null)}
          />
        </div>

        <div
          className="wl-preview"
          style={{ ['--cor-primaria' as string]: HEX.test(corPrimaria) ? corPrimaria : '#660099' }}
        >
          <div className="wl-preview-header">
            {logoExibido && (
              <img
                src={logoExibido}
                alt={previaLogo ? 'Novo logo (ainda não salvo)' : 'Logo atual'}
                className="app-logo"
              />
            )}
            <strong>{nomeSistema || 'Acervo de Termos de Adesão'}</strong>
          </div>
          <p className="muted">
            Prévia do cabeçalho.{' '}
            {previaLogo
              ? 'Novo logo escolhido — clique em Salvar para aplicar.'
              : logoSalvo
                ? 'Logo atual salvo.'
                : 'Nenhum logo salvo.'}
          </p>
        </div>

        {mensagem && (
          <p className={mensagem.tipo === 'ok' ? 'notice notice-ok' : 'notice'}>{mensagem.texto}</p>
        )}

        <div className="row-actions">
          <button className="btn btn-primary" type="submit" disabled={enviando}>
            {enviando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </div>
  );
}
